import { useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { PackageCheck, PackageX, AlertTriangle } from "lucide-react"
import { Role, getRoleCode } from "constant/Role"
import { useConfirmOrderDeliveryMutation } from "shared/redux/endpoints/procurementApprovals"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { Card, Button, Checkbox, Input, NumberInput, Textarea, Select, Alert, FileSlot } from "../../ui"
import { DELIVERY_REJECT_REASON_OPTIONS, DELIVERY_NOTIFY_OPTIONS, PO_OPEN_FOR_DELIVERY } from "../../constants"
import { getApiErrorMessage } from "../../orderSubmitFeedback"

// Matched against the *active* role, not every role on the JWT: holding
// ROLE_PROCUREMENT somewhere in your token shouldn't let you confirm another
// person's delivery while you're working as LXO. The order's own requester is
// always allowed regardless of which role they're wearing (see isOwner).
const CAN_CONFIRM_ROLES = [
  Role.PROCUREMENT,
  Role.PROCUREMENT_MANAGER,
  Role.ADMIN,
  Role.SUPPER_ADMIN,
]

const reasonLabel = (code) =>
  DELIVERY_REJECT_REASON_OPTIONS.find((o) => o.value === code)?.label || code

// "Received delivery" section on an approved OR. The requester (or procurement)
// confirms or rejects the goods delivered against the OR's open Purchase Order.
// Backend resolves the PO, updates its delivery_status, and — on "received" —
// nudges the PO issued → receiving. Hidden entirely unless the OR is approved.
const DeliveryConfirmationCard = ({ order, orderId }) => {
  const myId = useSelector((s) => s.auth?.user?.userId ?? s.auth?.user?.id)
  const activeRoleCode = useSelector((s) => getRoleCode(s.auth?.role))
  const [confirmDelivery, { isLoading }] = useConfirmOrderDeliveryMutation()
  // isLoading only flips on the next render; a fast double-click would post twice
  // and the BE has no dedupe (two delivery records, two notification emails).
  const sendingRef = useRef(false)

  const [mode, setMode] = useState("received") // "received" | "rejected"
  const [received, setReceived] = useState(false)
  const [purchaseId, setPurchaseId] = useState("")
  const [reasonCode, setReasonCode] = useState("")
  const [note, setNote] = useState("")
  const [file, setFile] = useState(null)
  const [notifyTargets, setNotifyTargets] = useState("pro")
  const [recordQty, setRecordQty] = useState(false)
  const [qty, setQty] = useState({}) // order line id -> { received, note }

  const purchases = order?.purchases || []
  const openPOs = useMemo(
    () => purchases.filter((p) => PO_OPEN_FOR_DELIVERY.includes(p.status)),
    [purchases]
  )
  const recordedPO = useMemo(
    () => purchases.find((p) => ["received", "partial", "rejected"].includes(p.deliveryStatus)),
    [purchases]
  )

  if (order?.status !== "approved") return null

  const isOwner =
    order.requesterId != null && String(order.requesterId) === String(myId)
  const canConfirm = isOwner || CAN_CONFIRM_ROLES.includes(activeRoleCode)

  // ── Last recorded outcome ───────────────────────────────────────────────
  // The backend accepts repeated reports for one PO (partial deliveries,
  // follow-ups); the latest call decides delivery_status. So this is only the
  // whole card when there's nothing left to act on.
  const recordedRejected = recordedPO?.deliveryStatus === "rejected"
  const recordedPartial = recordedPO?.orderReceiptStatus === "partial" || recordedPO?.deliveryStatus === "partial"
  const lineProgress =
    recordedPO?.orderTotalLines > 0 ? `${recordedPO.orderReceivedLines ?? 0}/${recordedPO.orderTotalLines} lines fully received` : ""
  const recordedAlert = recordedPO && (
    <div style={{ marginBottom: 14 }}>
      <Alert
        tone={recordedRejected || recordedPartial ? "warn" : "success"}
        icon={recordedRejected || recordedPartial ? <AlertTriangle aria-hidden /> : <PackageCheck aria-hidden />}
        title={
          recordedRejected
            ? `Delivery rejected for ${recordedPO.code}`
            : recordedPartial
            ? `Partially received for ${recordedPO.code}`
            : `Delivery confirmed for ${recordedPO.code}`
        }
      >
        {recordedRejected
          ? "Procurement has been notified and is following up with the supplier."
          : recordedPartial
          ? `${lineProgress ? `${lineProgress}. ` : ""}Report the next delivery below once the rest arrives.`
          : "The purchase order has moved to receiving — you can still report another delivery or a follow-up below."}
      </Alert>
    </div>
  )
  if (recordedPO && (openPOs.length === 0 || !canConfirm)) {
    return (
      <Card
        icon={recordedRejected ? <PackageX aria-hidden /> : <PackageCheck aria-hidden />}
        title="Goods receipt"
      >
        {recordedAlert}
      </Card>
    )
  }

  // ── No PO to receive against yet ───────────────────────────────────────
  if (openPOs.length === 0) {
    return (
      <Card icon={<PackageCheck aria-hidden />} title="Goods receipt">
        <p className="pc-muted">
          Procurement hasn&apos;t issued a purchase order for this request yet. You&apos;ll
          be able to confirm delivery here once it&apos;s issued.
        </p>
      </Card>
    )
  }

  // ── Read-only for viewers who can't act ────────────────────────────────
  if (!canConfirm) {
    return (
      <Card icon={<PackageCheck aria-hidden />} title="Goods receipt">
        <p className="pc-muted">Awaiting delivery confirmation from the requester.</p>
      </Card>
    )
  }

  const needsPoPick = openPOs.length > 1
  const targetPoId = needsPoPick ? purchaseId : openPOs[0].id

  // Quantity by item: default received = ordered, so only the lines the user
  // changes become missing/excess. Each delivery reports its own quantities
  // (BE sums the batches), and "received" requires the list.
  const qtyRows = (order.lines || [])
    .filter((l) => l.id != null)
    .map((l) => {
      const ordered = Number(l.quantity) || 0
      const entry = qty[l.id] || {}
      const receivedQty = entry.received === undefined || entry.received === "" ? ordered : Number(entry.received)
      const status = receivedQty < ordered ? "missing" : receivedQty > ordered ? "excess" : null
      return { line: l, ordered, receivedQty, status, note: entry.note || "", rawReceived: entry.received ?? ordered }
    })
  const qtyValid = qtyRows.every((r) => Number.isFinite(r.receivedQty) && r.receivedQty >= 0)
  const showQty = mode === "received" || recordQty
  const hasQtyProblem = showQty && qtyRows.some((r) => r.status)
  const hasProblem = mode === "rejected" || hasQtyProblem

  const canSubmit =
    !isLoading &&
    (!needsPoPick || purchaseId) &&
    note.trim() &&
    file &&
    (!showQty || qtyValid) &&
    (mode === "received" ? received : reasonCode)

  const reset = () => {
    setReceived(false)
    setReasonCode("")
    setNote("")
    setFile(null)
    setPurchaseId("")
    setMode("received")
    setNotifyTargets("pro")
    setRecordQty(false)
    setQty({})
  }

  const handleSubmit = async () => {
    if (!canSubmit || sendingRef.current) return
    sendingRef.current = true
    try {
      await confirmDelivery({
        id: orderId,
        outcome: mode,
        purchaseId: targetPoId || undefined,
        note: note.trim() || undefined,
        reasonCode: mode === "rejected" ? reasonCode : undefined,
        file: file || undefined,
        lines: showQty
          ? qtyRows.map((r) => ({
              orderRequestLineId: r.line.id,
              receivedQty: r.receivedQty,
              qualityStatus: r.status || undefined,
              qualityNote: r.note.trim() || undefined,
            }))
          : undefined,
        notifyTargets: hasProblem ? notifyTargets : undefined,
      }).unwrap()
      showToastSuccess(
        mode === "received"
          ? "Delivery confirmed."
          : `Delivery issue reported (${reasonLabel(reasonCode)}). Procurement has been notified.`
      )
      reset()
    } catch (e) {
      showToastError(
        getApiErrorMessage(e, "Couldn't record this delivery update. Please try again.")
      )
    } finally {
      sendingRef.current = false
    }
  }

  return (
    <Card icon={<PackageCheck aria-hidden />} title="Goods receipt">
      {recordedAlert}
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <Button
          variant={mode === "received" ? "primary" : "secondary"}
          size="sm"
          onClick={() => setMode("received")}
          disabled={isLoading}
        >
          I received it
        </Button>
        <Button
          variant={mode === "rejected" ? "warn" : "secondary"}
          size="sm"
          onClick={() => setMode("rejected")}
          disabled={isLoading}
        >
          Report a problem
        </Button>
      </div>

      {needsPoPick && (
        <div style={{ marginBottom: 12 }}>
          <Select
            label="Purchase order"
            required
            placeholder="Which PO is this for?"
            value={purchaseId}
            onChange={setPurchaseId}
            options={openPOs.map((p) => ({
              value: String(p.id),
              label: `${p.code} · ${p.vendorName || "—"}`,
            }))}
          />
        </div>
      )}

      {mode === "received" ? (
        <>
          <Checkbox
            checked={received}
            onChange={setReceived}
            label="Received delivery — the goods arrived and are acceptable"
            disabled={isLoading}
          />
          <div style={{ marginTop: 12 }}>
            <Textarea
              label="Note"
              required
              rows={3}
              placeholder="Anything worth recording about the delivery"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </>
      ) : (
        <>
          <Select
            label="Reason"
            required
            placeholder="What's wrong with the delivery?"
            value={reasonCode}
            onChange={setReasonCode}
            options={DELIVERY_REJECT_REASON_OPTIONS}
          />
          <div style={{ marginTop: 12 }}>
            <Textarea
              label="Note"
              required
              rows={3}
              placeholder="Describe the issue — what was wrong, how many, etc."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </>
      )}

      <div style={{ marginTop: 12 }}>
        {mode === "rejected" && (
          <Checkbox
            checked={recordQty}
            onChange={setRecordQty}
            label="Record quantity by item (missing / excess)"
            disabled={isLoading}
          />
        )}
        {showQty && (
          <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
            {qtyRows.map((r) => (
              <div
                key={r.line.id}
                style={{ display: "grid", gridTemplateColumns: "minmax(0,2fr) 90px 110px minmax(0,2fr)", gap: 10, alignItems: "end" }}
              >
                <div>
                  <div className="pc-def-label">{r.line.itemName || r.line.offCatalogItemName || "—"}</div>
                  <div className="pc-muted">Ordered: {r.ordered}</div>
                </div>
                <NumberInput
                  label="Received this delivery"
                  value={r.rawReceived}
                  onChange={(v) => setQty((p) => ({ ...p, [r.line.id]: { ...p[r.line.id], received: v } }))}
                  disabled={isLoading}
                />
                <div style={{ paddingBottom: 8, fontWeight: 600, color: r.status ? "var(--pc-warn)" : "var(--pc-muted)" }}>
                  {r.status === "missing" ? "Missing" : r.status === "excess" ? "Excess" : "OK"}
                </div>
                <Input
                  label="Note"
                  placeholder={r.status ? "e.g. 2 short, supplier will deliver later" : ""}
                  value={r.note}
                  onChange={(e) => setQty((p) => ({ ...p, [r.line.id]: { ...p[r.line.id], note: e.target.value } }))}
                  disabled={isLoading}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {hasProblem && (
        <div style={{ marginTop: 12 }}>
          <Select
            label="Send the problem report to"
            allowEmpty={false}
            value={notifyTargets}
            onChange={setNotifyTargets}
            options={DELIVERY_NOTIFY_OPTIONS}
          />
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <FileSlot
          label="Delivery note / photo *"
          file={file}
          onSelect={setFile}
          disabled={isLoading}
        />
      </div>

      <div style={{ marginTop: 4 }}>
        <Button
          variant={mode === "received" ? "primary" : "warn"}
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {isLoading
            ? "Submitting…"
            : mode === "received"
            ? "Confirm delivery"
            : "Report delivery issue"}
        </Button>
      </div>
    </Card>
  )
}

export default DeliveryConfirmationCard
